import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactFlow, {
  Background,
  Controls,
  MiniMap,
  Handle,
  Position,
  useNodesState,
  useEdgesState,
  MarkerType,
} from 'reactflow';
import 'reactflow/dist/style.css';
import {
  LineChart, Line, XAxis, YAxis, Tooltip as RTooltip, CartesianGrid, ResponsiveContainer,
} from 'recharts';
import {
  Workflow, Search, X, Award, AlertTriangle, Sparkles, ArrowRight,
  Copy, Clock, MessageSquare, Layers, Lock, RotateCcw, Plus,
  Edit2, Trash2, Move, CopyPlus, MoreVertical, BarChart3, FileText, TrendingUp,
  Archive, Link as LinkIcon, Image as ImageIcon, Upload as UploadIcon, ExternalLink,
  Download, ChevronDown,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { exportFlowCanvas } from '../../utils/exportFlow';
import {
  listJourneyStages,
  listFlowEdges,
  createFlowEdge,
  deleteFlowEdge,
  listContents,
  createContent,
  updateContent,
  listCampaignTypes,
  listFlowNodes,
  createFlowNode,
  updateFlowNode,
  deleteFlowNode,
  duplicateFlowNode,
  moveFlowNode,
  archiveFlowNode,
  listCreatives,
  uploadCreative,
} from '../../utils/adminApi';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const TEAL = '#2EB4AC';
const PURPLE = '#7c3aed';
const MONTH_NAMES = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
const METRIC_KEYS = ['revenue','spend','sent','delivered','seen','clicks'];

// Layout
const STAGE_X_STEP = 320;
const STAGE_X_START = 60;
const CARD_H = 110;
const ROW_GAP = 16;
const STAGE_LANE_TOP = 30;
const CARDS_TOP = 110;

const performanceColor = (roi) => {
  if (roi >= 5) return { name: 'High',    bg: 'bg-emerald-50', border: 'border-emerald-300', dot: 'bg-emerald-500', text: 'text-emerald-700' };
  if (roi >= 2) return { name: 'Average', bg: 'bg-amber-50',   border: 'border-amber-300',   dot: 'bg-amber-500',   text: 'text-amber-700'   };
  return           { name: 'Low',     bg: 'bg-rose-50',    border: 'border-rose-300',    dot: 'bg-rose-500',    text: 'text-rose-700'    };
};

const fmtInr = (n) => `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(n || 0))}`;
const fmtCompact = (n) => {
  if (!n) return '0';
  if (n >= 1e7) return `${(n / 1e7).toFixed(1)}Cr`;
  if (n >= 1e5) return `${(n / 1e5).toFixed(1)}L`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}k`;
  return Math.round(n).toString();
};
// Compact ₹ formatter for scorecards (e.g. ₹28.5K, ₹1.2L, ₹4.3Cr)
const fmtInrCompact = (n) => {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(1)}K`;
  return `₹${new Intl.NumberFormat('en-IN', { maximumFractionDigits: 0 }).format(Math.round(v))}`;
};

// React Flow node component types (module-level — no re-creation per render)
const MilestoneNode = ({ data }) => (
  <div className="px-4 py-2.5 rounded-full bg-slate-900 text-white text-xs font-semibold shadow-md flex items-center gap-2" data-testid={`flow-node-milestone-${data.stageName}`}>
    <span className="inline-block w-1.5 h-1.5 rounded-full bg-teal-400" />
    {data.label}
    <Handle type="target" position={Position.Left}  style={{ background: '#1e293b', border: 'none', width: 8, height: 8 }} />
    <Handle type="source" position={Position.Right} style={{ background: '#1e293b', border: 'none', width: 8, height: 8 }} />
  </div>
);

const StageBucketNode = ({ data }) => (
  <div className="px-3 py-2 rounded-lg bg-white border-2 border-dashed border-teal-400 text-xs font-semibold text-teal-700 shadow-sm flex items-center gap-2 group" data-testid={`flow-node-bucket-${data.stageName}`}>
    <Layers size={12} />
    {data.label}
    {data.isAdmin && (
      <button
        onClick={(e) => { e.stopPropagation(); data.onAddNode(data.stageId); }}
        className="p-0.5 rounded hover:bg-teal-100 opacity-0 group-hover:opacity-100 transition-opacity"
        title="Add campaign node to this stage"
        data-testid={`bucket-add-${data.stageName}`}
      >
        <Plus size={11} />
      </button>
    )}
    <Handle type="target" position={Position.Left}  style={{ background: TEAL, border: 'none', width: 8, height: 8 }} />
    <Handle type="source" position={Position.Right} style={{ background: TEAL, border: 'none', width: 8, height: 8 }} />
  </div>
);

const CampaignNode = ({ data }) => {
  const node = data.node;
  const c = performanceColor(node.roi || 0);
  const [menu, setMenu] = useState(false);
  return (
    <div
      onClick={() => data.onClick(node)}
      className={`relative w-[230px] rounded-xl ${c.bg} ${c.border} border-2 shadow-sm hover:shadow-lg transition-shadow cursor-pointer group`}
      data-testid={`flow-node-campaign-${node.name}`}
    >
      <Handle type="target" position={Position.Left}  style={{ background: TEAL, border: 'none', width: 6, height: 6 }} />
      <Handle type="source" position={Position.Right} style={{ background: TEAL, border: 'none', width: 6, height: 6 }} />
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="text-[12px] font-bold text-slate-900 leading-tight break-words">
            {node.name}
            {node.status && node.status !== 'active' && (
              <span className="ml-1 text-[8px] uppercase font-bold px-1 py-0.5 rounded bg-slate-200 text-slate-600 tracking-wider">{node.status}</span>
            )}
          </div>
          {data.isAdmin ? (
            <button onClick={(e) => { e.stopPropagation(); setMenu((s) => !s); }} className="flex-shrink-0 p-0.5 rounded hover:bg-white/70 opacity-0 group-hover:opacity-100 transition-opacity" data-testid={`node-menu-${node.name}`}>
              <MoreVertical size={12} className="text-slate-500" />
            </button>
          ) : (
            <span className={`flex-shrink-0 inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold ${c.text} bg-white`}>
              <span className={`inline-block w-1.5 h-1.5 rounded-full ${c.dot}`} />
              {c.name}
            </span>
          )}
        </div>
        <div className="grid grid-cols-2 gap-1.5 text-[10px]">
          <Tile label="Revenue"   value={fmtInr((node.metrics || {}).revenue)} />
          <Tile label="ROAS"      value={`${node.roi || 0}x`} />
          <Tile label="Open Rate" value={`${node.seen_rate || 0}%`} />
          <Tile label="CTR"       value={`${node.ctr || 0}%`} />
        </div>
      </div>
      {data.isAdmin && menu && (
        <div className="absolute top-7 right-1 z-30 bg-white border border-slate-200 rounded-lg shadow-lg py-1 w-36" onClick={(e) => e.stopPropagation()}>
          <NodeMenuItem icon={Edit2}    label="Edit"      onClick={() => { setMenu(false); data.onEdit(node); }}      testid={`node-edit-${node.name}`} />
          <NodeMenuItem icon={Move}     label="Move..."   onClick={() => { setMenu(false); data.onMove(node); }}      testid={`node-move-${node.name}`} />
          <NodeMenuItem icon={CopyPlus} label="Duplicate" onClick={() => { setMenu(false); data.onDuplicate(node); }} testid={`node-duplicate-${node.name}`} />
          <NodeMenuItem icon={Archive}  label="Archive"   onClick={() => { setMenu(false); data.onArchive(node); }}   testid={`node-archive-${node.name}`} />
          <div className="border-t border-slate-100 my-0.5" />
          <NodeMenuItem icon={Trash2}   label="Delete"    onClick={() => { setMenu(false); data.onDelete(node); }}    danger testid={`node-delete-${node.name}`} />
        </div>
      )}
    </div>
  );
};

const NodeMenuItem = ({ icon: Icon, label, onClick, danger, testid }) => (
  <button onClick={onClick} data-testid={testid}
    className={`w-full flex items-center gap-2 px-2.5 py-1.5 text-[11px] text-left ${danger ? 'text-rose-600 hover:bg-rose-50' : 'text-slate-700 hover:bg-slate-50'}`}>
    <Icon size={11} />
    {label}
  </button>
);

const Tile = ({ label, value }) => (
  <div className="bg-white/70 rounded-md px-2 py-1">
    <div className="text-slate-400">{label}</div>
    <div className="font-bold text-slate-900 tabular-nums">{value}</div>
  </div>
);

const StageHeaderNode = ({ data }) => (
  <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-slate-400 whitespace-nowrap" style={{ pointerEvents: 'none' }}>
    {data.label}
  </div>
);

const nodeTypes = {
  milestone: MilestoneNode,
  bucket: StageBucketNode,
  campaign: CampaignNode,
  stageHeader: StageHeaderNode,
};

// ---------- Static suggested opportunities ----------
const SUGGESTED = [
  { name: 'Collection Exit Flow',     impact: 'High',    priority: 'P0', desc: 'Re-engage users leaving collection pages without product view.' },
  { name: 'PDP Exit Flow',            impact: 'High',    priority: 'P0', desc: 'Recover users who view a PDP but exit without adding to cart.' },
  { name: 'Payment Failure Recovery', impact: 'Medium',  priority: 'P1', desc: 'Trigger after a failed checkout attempt.' },
  { name: 'COD Confirmation Flow',    impact: 'Medium',  priority: 'P1', desc: 'Reduce RTO by confirming COD orders via WhatsApp.' },
  { name: 'Review Collection Flow',   impact: 'Medium',  priority: 'P1', desc: 'Automated review ask 5 days post-delivery.' },
  { name: 'Repurchase Flow',          impact: 'High',    priority: 'P0', desc: 'Re-engage at typical repurchase interval (45/60/90d).' },
  { name: 'Winback Flow',             impact: 'High',    priority: 'P0', desc: 'Reactivate dormant customers (>120d).' },
  { name: 'Cross-Sell Flow',          impact: 'Medium',  priority: 'P1', desc: 'Recommend complementary products post-purchase.' },
];
const impactColor = (i) => ({
  High:   'bg-emerald-100 text-emerald-700 border-emerald-200',
  Medium: 'bg-amber-100 text-amber-700 border-amber-200',
  Low:    'bg-slate-100 text-slate-600 border-slate-200',
}[i] || 'bg-slate-100 text-slate-600 border-slate-200');

// ====================== Main Page ======================
const FlowBuilder = ({ showToast }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const isViewer = user?.role === 'viewer';

  const [stages, setStages]     = useState([]);
  const [edgesDb, setEdgesDb]   = useState([]);
  const [flowNodes, setFlowNodes] = useState([]);
  const [contents, setContents] = useState([]);
  const [creatives, setCreatives] = useState([]);
  const [types, setTypes]       = useState([]);
  const [loading, setLoading]   = useState(true);
  const [drawerNode, setDrawerNode] = useState(null);

  const [editingNode, setEditingNode] = useState(null);
  const [movingNode, setMovingNode]   = useState(null);
  const [editingContent, setEditingContent] = useState(null);

  // Filters
  const [search, setSearch] = useState('');
  const [stageFilter, setStageFilter] = useState('all');

  // Export dropdown state
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Close export menu on outside click
  useEffect(() => {
    if (!exportMenuOpen) return undefined;
    const onDocClick = (e) => {
      if (!e.target.closest?.('[data-testid="flow-builder-export-wrap"]')) {
        setExportMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [exportMenuOpen]);

  const handleExport = useCallback(async (mode, format) => {
    setExportMenuOpen(false);
    try {
      setExporting(true);
      await exportFlowCanvas(mode, format, 'flow-builder');
      showToast?.(`Exported ${mode === 'full' ? 'full canvas' : 'current view'} as ${format.toUpperCase()}`, 'success');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Export failed', err);
      showToast?.('Export failed. Please try again.', 'error');
    } finally {
      setExporting(false);
    }
  }, [showToast]);

  const reloadAll = useCallback(async () => {
    setLoading(true);
    try {
      const [stagesRes, edgesRes, nodesRes, contentsRes, creativesRes, typesRes] = await Promise.all([
        listJourneyStages(),
        listFlowEdges(),
        listFlowNodes({ include_archived: true }),
        listContents().catch(() => ({ data: [] })),
        listCreatives({ include_archived: true }).catch(() => ({ data: [] })),
        listCampaignTypes().catch(() => ({ data: [] })),
      ]);
      setStages(Array.isArray(stagesRes.data) ? stagesRes.data : []);
      setEdgesDb(Array.isArray(edgesRes.data) ? edgesRes.data : []);
      setFlowNodes(Array.isArray(nodesRes.data) ? nodesRes.data : []);
      setContents(Array.isArray(contentsRes.data) ? contentsRes.data : []);
      setCreatives(Array.isArray(creativesRes.data) ? creativesRes.data : []);
      setTypes(Array.isArray(typesRes.data) ? typesRes.data : []);
    } catch {
      showToast?.('Failed to load flow data', 'error');
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { reloadAll(); }, [reloadAll]);

  // Keep drawerNode synced with refreshed node data
  useEffect(() => {
    if (drawerNode) {
      const fresh = flowNodes.find((n) => n.id === drawerNode.id);
      if (fresh) setDrawerNode(fresh);
    }
     
  }, [flowNodes]);

  const stageList = useMemo(() => {
    return stages.slice()
      .sort((a, b) => (a.order || 99) - (b.order || 99))
      .map((s, i) => ({ id: s.id, name: s.name, kind: s.kind, order: s.order, x: STAGE_X_START + i * STAGE_X_STEP }));
  }, [stages]);

  const stageById = useMemo(() => {
    const m = new Map(); stageList.forEach((s) => m.set(s.id, s)); return m;
  }, [stageList]);

  // Group nodes by stage (DB-driven — no regex)
  const nodesByStage = useMemo(() => {
    const buckets = {};
    stageList.forEach((s) => { if (s.kind === 'campaign_bucket') buckets[s.id] = []; });
    flowNodes.forEach((n) => {
      if (n.status === 'archived') return;
      const sid = n.stage_id;
      if (sid && buckets[sid] !== undefined) buckets[sid].push(n);
    });
    return buckets;
  }, [stageList, flowNodes]);

  const visibleByStage = useMemo(() => {
    const out = {};
    Object.entries(nodesByStage).forEach(([k, v]) => {
      let list = v;
      if (search) list = list.filter((n) => (n.name || '').toLowerCase().includes(search.toLowerCase()) || (n.brand || '').toLowerCase().includes(search.toLowerCase()));
      if (stageFilter !== 'all' && stageFilter !== k) list = [];
      out[k] = list;
    });
    return out;
  }, [nodesByStage, search, stageFilter]);

  const unassignedNodes = useMemo(() => {
    return flowNodes.filter((n) => n.status !== 'archived' && (!n.stage_id || !stageById.has(n.stage_id)));
  }, [flowNodes, stageById]);

  // ---------- Admin actions ----------
  const openAddNode = useCallback((stageId) => {
    setEditingNode({
      _isNew: true, id: null,
      name: '', brand: '',
      stage_id: stageId || (stageList.find((s) => s.kind === 'campaign_bucket') || {}).id || null,
      type_id: null, content_id: null,
      status: 'active', description: '',
      metrics: { revenue:0, spend:0, sent:0, delivered:0, seen:0, clicks:0 },
      monthly_metrics: [],
    });
  }, [stageList]);

  const onDeleteNode = useCallback(async (node) => {
    if (!window.confirm(`Delete "${node.name}"? This cannot be undone.`)) return;
    try { await deleteFlowNode(node.id); showToast?.('Node deleted', 'success'); reloadAll(); }
    catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  }, [reloadAll, showToast]);

  const onArchiveNode = useCallback(async (node) => {
    try {
      const archived = node.status !== 'archived';
      await archiveFlowNode(node.id, archived);
      showToast?.(archived ? 'Node archived' : 'Node restored', 'success');
      reloadAll();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  }, [reloadAll, showToast]);

  const onDuplicateNode = useCallback(async (node) => {
    try { await duplicateFlowNode(node.id); showToast?.('Node duplicated', 'success'); reloadAll(); }
    catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  }, [reloadAll, showToast]);

  // ---------- React Flow data ----------
  const computedNodes = useMemo(() => {
    const out = [];
    stageList.forEach((s) => {
      out.push({ id: `header-${s.id}`, type: 'stageHeader', position: { x: s.x, y: -40 },
        data: { label: s.name }, draggable: false, selectable: false, focusable: false });
      if (s.kind === 'milestone') {
        out.push({ id: s.id, type: 'milestone', position: { x: s.x, y: STAGE_LANE_TOP },
          data: { label: s.name, stageName: s.name }, draggable: false });
      } else {
        out.push({ id: s.id, type: 'bucket', position: { x: s.x, y: STAGE_LANE_TOP },
          data: { label: s.name, stageName: s.name, stageId: s.id, isAdmin, onAddNode: openAddNode },
          draggable: false });
        const list = visibleByStage[s.id] || [];
        list.forEach((n, idx) => {
          out.push({
            id: `c-${n.id}`,
            type: 'campaign',
            position: { x: s.x, y: CARDS_TOP + idx * (CARD_H + ROW_GAP) },
            data: { node: n, isAdmin, onClick: setDrawerNode, onEdit: setEditingNode, onMove: setMovingNode, onDuplicate: onDuplicateNode, onDelete: onDeleteNode, onArchive: onArchiveNode },
            draggable: false,
          });
        });
      }
    });
    return out;
  }, [stageList, visibleByStage, isAdmin, openAddNode, onDuplicateNode, onDeleteNode, onArchiveNode]);

  const computedEdges = useMemo(() => {
    const out = [];
    edgesDb.forEach((e) => {
      out.push({
        id: e.id, source: e.source, target: e.target,
        type: 'smoothstep', label: e.label || undefined,
        labelStyle: { fontSize: 10, fill: '#475569' },
        style: { stroke: '#94a3b8', strokeWidth: 1.5, strokeDasharray: '4 4' },
        markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' },
        data: { dbEdge: true },
      });
    });
    stageList.filter((s) => s.kind === 'campaign_bucket').forEach((s) => {
      (visibleByStage[s.id] || []).forEach((n) => {
        out.push({ id: `vis-${s.id}-${n.id}`, source: s.id, target: `c-${n.id}`, type: 'smoothstep',
          style: { stroke: TEAL, strokeWidth: 1.2, opacity: 0.5 }, deletable: false, selectable: false });
      });
    });
    return out;
  }, [edgesDb, stageList, visibleByStage]);

  const [nodes, setNodes, onNodesChange] = useNodesState(computedNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(computedEdges);
  useEffect(() => { setNodes(computedNodes); }, [computedNodes, setNodes]);
  useEffect(() => { setEdges(computedEdges); }, [computedEdges, setEdges]);

  // ---------- Edge create / delete ----------
  const onConnect = useCallback(async (params) => {
    if (!isAdmin) { showToast?.('Only admins can edit the flow', 'error'); return; }
    const stageIds = new Set(stageList.map((s) => s.id));
    if (!stageIds.has(params.source) || !stageIds.has(params.target)) {
      showToast?.('Only stage-to-stage connections can be saved', 'error'); return;
    }
    try {
      const res = await createFlowEdge({ source: params.source, target: params.target });
      setEdgesDb((prev) => prev.some((e) => e.id === res.data.id) ? prev : [...prev, res.data]);
      showToast?.('Connection saved', 'success');
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  }, [isAdmin, stageList, showToast]);

  const onEdgesDelete = useCallback(async (toDelete) => {
    if (!isAdmin) return;
    for (const e of toDelete) {
      if (!e?.data?.dbEdge) continue;
      try { await deleteFlowEdge(e.id); setEdgesDb((prev) => prev.filter((x) => x.id !== e.id)); }
      catch { showToast?.('Failed to delete connection', 'error'); }
    }
  }, [isAdmin, showToast]);

  const resetEdgesToDefault = useCallback(async () => {
    if (!isAdmin) return;
    if (!window.confirm('Reset all stage connections to the default linear flow (order-based)? Custom edges will be lost.')) return;
    try {
      await Promise.all(edgesDb.map((e) => deleteFlowEdge(e.id).catch(() => null)));
      const sorted = stageList.slice().sort((a, b) => (a.order || 99) - (b.order || 99));
      const created = [];
      for (let i = 0; i < sorted.length - 1; i++) {
        const r = await createFlowEdge({ source: sorted[i].id, target: sorted[i + 1].id });
        created.push(r.data);
      }
      setEdgesDb(created);
      showToast?.('Flow reset to default linear path', 'success');
    } catch { showToast?.('Failed to reset flow', 'error'); }
  }, [isAdmin, edgesDb, stageList, showToast]);

  // ---------- Node save ----------
  const saveNode = useCallback(async (payload) => {
    try {
      if (payload._isNew) {
        await createFlowNode({
          name: payload.name, brand: payload.brand,
          stage_id: payload.stage_id || null,
          type_id: payload.type_id || null,
          content_id: payload.content_id || null,
          creative_id: payload.creative_id || null,
          status: payload.status || 'active',
          description: payload.description || '',
          metrics: payload.metrics, monthly_metrics: payload.monthly_metrics,
        });
        showToast?.('Node created', 'success');
      } else {
        await updateFlowNode(payload.id, {
          name: payload.name, brand: payload.brand,
          stage_id: payload.stage_id || null,
          type_id: payload.type_id || null,
          content_id: payload.content_id || null,
          creative_id: payload.creative_id || null,
          status: payload.status, description: payload.description || '',
          metrics: payload.metrics, monthly_metrics: payload.monthly_metrics,
        });
        showToast?.('Node updated', 'success');
      }
      setEditingNode(null);
      reloadAll();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed to save', 'error'); }
  }, [reloadAll, showToast]);

  const submitMove = useCallback(async (newStageId) => {
    if (!movingNode) return;
    try {
      await moveFlowNode(movingNode.id, newStageId);
      showToast?.('Node moved', 'success');
      setMovingNode(null);
      reloadAll();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  }, [movingNode, reloadAll, showToast]);

  // ---------- Link / Create content from drawer ----------
  const linkContentToNode = useCallback(async (nodeId, contentId) => {
    try {
      await updateFlowNode(nodeId, { content_id: contentId || null });
      showToast?.(contentId ? 'Content linked' : 'Content unlinked', 'success');
      reloadAll();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  }, [reloadAll, showToast]);

  const linkCreativeToNode = useCallback(async (nodeId, creativeId) => {
    try {
      await updateFlowNode(nodeId, { creative_id: creativeId || null });
      showToast?.(creativeId ? 'Creative linked' : 'Creative removed', 'success');
      reloadAll();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  }, [reloadAll, showToast]);

  const uploadAndLinkCreative = useCallback(async (nodeId, file, name) => {
    try {
      const res = await uploadCreative(file, { name });
      const cid = res.data?.id;
      if (cid) await updateFlowNode(nodeId, { creative_id: cid });
      showToast?.('Creative uploaded & linked', 'success');
      reloadAll();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Upload failed', 'error'); }
  }, [reloadAll, showToast]);

  const saveContentRow = useCallback(async (payload, linkToNodeId) => {
    try {
      let cid = payload.id;
      if (payload.id) {
        const r = await updateContent(payload.id, { name: payload.name, body: payload.body, tags: payload.tags || '' });
        cid = r.data?.id || payload.id;
      } else {
        const r = await createContent({ name: payload.name, body: payload.body, tags: payload.tags || '' });
        cid = r.data?.id;
      }
      if (linkToNodeId && cid) {
        await updateFlowNode(linkToNodeId, { content_id: cid });
      }
      showToast?.(payload.id ? 'Content updated' : 'Content created', 'success');
      setEditingContent(null);
      reloadAll();
    } catch (e) { showToast?.(e.response?.data?.detail || 'Failed', 'error'); }
  }, [reloadAll, showToast]);

  // ---------- Top stats ----------
  const visibleNodes = useMemo(() => Object.values(visibleByStage).flat(), [visibleByStage]);

  const stats = useMemo(() => {
    const total = visibleNodes.reduce((s, n) => s + ((n.metrics || {}).revenue || 0), 0);
    const totalSpend = visibleNodes.reduce((s, n) => s + ((n.metrics || {}).spend || 0), 0);
    const sorted = visibleNodes.slice().sort((a, b) => ((b.metrics || {}).revenue || 0) - ((a.metrics || {}).revenue || 0));
    const sortedRoas = visibleNodes.slice().sort((a, b) => (b.roi || 0) - (a.roi || 0));
    return {
      totalFlows: visibleNodes.length,
      totalRevenue: total,
      totalSpend,
      bestPerf: sorted[0] || null,
      worstPerf: sorted[sorted.length - 1] || null,
      bestRoas: sortedRoas[0] || null,
    };
  }, [visibleNodes]);

  return (
    <div className="p-6 max-w-[1600px] mx-auto" data-testid="flow-builder-page">
      <div className="mb-4 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-2">
          <Workflow size={20} style={{ color: TEAL }} />
          <h2 className="text-base font-semibold text-slate-700">Journey canvas</h2>
        </div>
        <div className="flex items-center gap-2">
          {/* Export dropdown — non-viewer roles */}
          {!isViewer && (
            <div className="relative" data-testid="flow-builder-export-wrap">
              <button
                onClick={() => setExportMenuOpen((v) => !v)}
                disabled={exporting}
                data-testid="flow-builder-export-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50"
              >
                <Download size={12} />
                {exporting ? 'Exporting…' : 'Export'}
                <ChevronDown size={11} className="text-slate-400" />
              </button>
              {exportMenuOpen && (
                <div
                  className="absolute right-0 top-full mt-1 w-56 bg-white border border-slate-200 rounded-lg shadow-xl z-30 overflow-hidden"
                  data-testid="flow-builder-export-menu"
                >
                  <div className="px-3 py-2 bg-slate-50 border-b border-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Current View
                  </div>
                  <button
                    onClick={() => handleExport('viewport', 'png')}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-teal-50 hover:text-teal-700 flex items-center gap-2"
                    data-testid="flow-builder-export-viewport-png"
                  >
                    <FileText size={12} /> Viewport as PNG
                  </button>
                  <button
                    onClick={() => handleExport('viewport', 'pdf')}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-teal-50 hover:text-teal-700 flex items-center gap-2"
                    data-testid="flow-builder-export-viewport-pdf"
                  >
                    <FileText size={12} /> Viewport as PDF
                  </button>
                  <div className="px-3 py-2 bg-slate-50 border-y border-slate-100 text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                    Full Canvas
                  </div>
                  <button
                    onClick={() => handleExport('full', 'png')}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-teal-50 hover:text-teal-700 flex items-center gap-2"
                    data-testid="flow-builder-export-full-png"
                  >
                    <FileText size={12} /> Full Canvas as PNG
                  </button>
                  <button
                    onClick={() => handleExport('full', 'pdf')}
                    className="w-full text-left px-3 py-2 text-xs text-slate-700 hover:bg-teal-50 hover:text-teal-700 flex items-center gap-2"
                    data-testid="flow-builder-export-full-pdf"
                  >
                    <FileText size={12} /> Full Canvas as PDF
                  </button>
                </div>
              )}
            </div>
          )}

          {isAdmin && (
            <>
              <button onClick={() => openAddNode(null)} data-testid="flow-builder-add-node-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold text-white rounded-lg hover:opacity-90"
                style={{ backgroundColor: TEAL }}>
                <Plus size={12} /> Add node
              </button>
              <button onClick={resetEdgesToDefault} data-testid="flow-builder-reset-btn"
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50">
                <RotateCcw size={12} /> Reset flow
              </button>
            </>
          )}
          {isViewer && (
            <span className="flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-600">
              <Lock size={10} /> View only
            </span>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-4" data-testid="flow-builder-stats">
        <StatCard label="Total Flows" value={stats.totalFlows} />
        <StatCard label="Total Revenue" value={fmtInr(stats.totalRevenue)} accent />
        <StatCard label="Total Spend" value={fmtInrCompact(stats.totalSpend)} data-testid="stat-total-spend" />
        <StatCard label="Best Performing" value={stats.bestPerf?.name || '—'} sub={stats.bestPerf ? fmtInr((stats.bestPerf.metrics || {}).revenue) : ''} />
        <StatCard label="Best ROAS" value={stats.bestRoas?.name || '—'} sub={stats.bestRoas ? `${stats.bestRoas.roi}x` : ''} />
        <StatCard label="Lowest Perf." value={stats.worstPerf?.name || '—'} sub={stats.worstPerf ? fmtInr((stats.worstPerf.metrics || {}).revenue) : ''} />
      </div>

      <div className="flex flex-col md:flex-row gap-3 mb-4 bg-white border border-slate-200 rounded-xl p-3">
        <div className="relative flex-1">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" placeholder="Search node or brand..." value={search} onChange={(e) => setSearch(e.target.value)}
            data-testid="flow-builder-search"
            className="w-full pl-9 pr-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
        </div>
        <select value={stageFilter} onChange={(e) => setStageFilter(e.target.value)} data-testid="flow-builder-stage-filter"
          className="px-3 py-2 text-sm bg-slate-50 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
          <option value="all">All Stages</option>
          {stageList.filter((s) => s.kind === 'campaign_bucket').map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </select>
      </div>

      {isAdmin && (
        <div className="mb-3 px-3 py-2 bg-teal-50 border border-teal-200 rounded-lg text-[11px] text-teal-800 flex items-center gap-2">
          <Sparkles size={12} />
          <span><strong>Edit mode:</strong> drag stage handles to wire connections, hover a campaign card for actions, or click <strong>+</strong> on a stage to add a new campaign. Metrics are editable per node — bulk upload via <strong>Admin → Upload Center</strong>.</span>
        </div>
      )}

      <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden" style={{ height: 580 }} data-testid="flow-builder-canvas">
        {loading ? (
          <div className="h-full flex items-center justify-center text-slate-400 text-sm">Loading flow...</div>
        ) : stageList.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-slate-400 text-sm gap-2">
            <Layers size={32} className="opacity-40" />
            <p>No journey stages defined yet.</p>
            <p className="text-xs">Add stages in <span className="font-semibold">Admin → Journey Stages</span>.</p>
          </div>
        ) : (
          <ReactFlow
            nodes={nodes} edges={edges} nodeTypes={nodeTypes}
            onNodesChange={onNodesChange} onEdgesChange={onEdgesChange}
            onConnect={onConnect} onEdgesDelete={onEdgesDelete}
            fitView fitViewOptions={{ padding: 0.15, maxZoom: 1 }}
            minZoom={0.15} maxZoom={2}
            translateExtent={[[-3000, -1000], [20000, 10000]]}
            nodeExtent={[[-3000, -1000], [20000, 10000]]}
            nodesDraggable={false}
            nodesConnectable={isAdmin}
            elementsSelectable
            edgesUpdatable={isAdmin} edgesFocusable={isAdmin}
            deleteKeyCode={isAdmin ? ['Backspace', 'Delete'] : null}
            panOnDrag zoomOnScroll proOptions={{ hideAttribution: true }}
            defaultEdgeOptions={{ type: 'smoothstep', markerEnd: { type: MarkerType.ArrowClosed, color: '#94a3b8' } }}
          >
            <Background gap={20} size={1} color="#cbd5e1" />
            <Controls showInteractive={false} />
            <MiniMap pannable zoomable maskColor="rgba(255,255,255,0.7)"
              nodeColor={(n) => n.type === 'milestone' ? '#1e293b' : n.type === 'bucket' ? TEAL : '#cbd5e1'} />
          </ReactFlow>
        )}
      </div>

      {unassignedNodes.length > 0 && (
        <div className="mt-4 bg-amber-50 border border-amber-200 rounded-xl p-3" data-testid="flow-builder-unassigned">
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle size={14} className="text-amber-600" />
            <span className="text-xs font-bold text-amber-800">Unassigned nodes ({unassignedNodes.length})</span>
            <span className="text-[10px] text-amber-700">— assign a stage to make them appear on the canvas.</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {unassignedNodes.map((n) => (
              <button key={n.id} onClick={() => setDrawerNode(n)}
                className="px-2 py-1 bg-white border border-amber-300 rounded-md text-[11px] text-slate-700 hover:bg-amber-100"
                data-testid={`unassigned-node-${n.name}`}>
                {n.name}
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-slate-500">
        <LegendDot color="bg-emerald-500" label="High Performing (ROAS ≥ 5)" />
        <LegendDot color="bg-amber-500" label="Average (2 ≤ ROAS < 5)" />
        <LegendDot color="bg-rose-500" label="Underperforming (ROAS < 2)" />
        <span className="text-slate-400">·</span>
        <span>Click any campaign card for content, analytics & metrics.</span>
      </div>

      <div className="mt-8" data-testid="flow-builder-suggestions">
        <div className="flex items-center gap-2 mb-3">
          <Sparkles size={16} style={{ color: TEAL }} />
          <h2 className="text-base font-bold text-slate-900">Suggested Automation Opportunities</h2>
          <span className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">Coming soon</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
          {SUGGESTED.map((s) => (
            <div key={s.name} className="bg-white border border-slate-200 rounded-xl p-3 hover:border-teal-300 transition-colors">
              <div className="flex items-start justify-between mb-1.5">
                <div className="text-sm font-semibold text-slate-900">{s.name}</div>
                <span className="text-[10px] font-bold text-slate-400">{s.priority}</span>
              </div>
              <p className="text-[11px] text-slate-500 mb-2 leading-relaxed">{s.desc}</p>
              <span className={`inline-block px-2 py-0.5 rounded-full text-[10px] font-semibold border ${impactColor(s.impact)}`}>
                {s.impact} impact
              </span>
            </div>
          ))}
        </div>
      </div>

      {drawerNode && (
        <NodeDrawer
          node={drawerNode}
          isAdmin={isAdmin}
          contentRow={contents.find((c) => c.id === drawerNode.content_id) || null}
          creativeRow={creatives.find((c) => c.id === drawerNode.creative_id) || null}
          stage={stageById.get(drawerNode.stage_id)}
          campaignType={types.find((t) => t.id === drawerNode.type_id) || null}
          allContents={contents}
          allCreatives={creatives}
          onClose={() => setDrawerNode(null)}
          onEdit={() => setEditingNode(drawerNode)}
          onLinkContent={(cid) => linkContentToNode(drawerNode.id, cid)}
          onCreateContent={() => setEditingContent({ _isNew: true, _linkTo: drawerNode.id, name: drawerNode.name, body: '', tags: '' })}
          onEditContent={(c) => setEditingContent({ ...c, _linkTo: null })}
          onLinkCreative={(cid) => linkCreativeToNode(drawerNode.id, cid)}
          onUploadCreative={(file, name) => uploadAndLinkCreative(drawerNode.id, file, name)}
          showToast={showToast}
        />
      )}

      {editingNode && isAdmin && (
        <NodeEditModal
          node={editingNode}
          stages={stageList.filter((s) => s.kind === 'campaign_bucket')}
          types={types}
          contents={contents}
          creatives={creatives}
          onClose={() => setEditingNode(null)}
          onSave={saveNode}
        />
      )}

      {movingNode && isAdmin && (
        <MoveNodeModal
          node={movingNode}
          stages={stageList.filter((s) => s.kind === 'campaign_bucket')}
          currentStageId={movingNode.stage_id}
          onClose={() => setMovingNode(null)}
          onSubmit={submitMove}
        />
      )}

      {editingContent && isAdmin && (
        <ContentEditModal
          content={editingContent}
          onClose={() => setEditingContent(null)}
          onSave={(payload) => saveContentRow(payload, editingContent._linkTo)}
        />
      )}
    </div>
  );
};

// ---------- Small UI helpers ----------
const StatCard = ({ label, value, sub, accent, 'data-testid': dataTestId }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-3" data-testid={dataTestId}>
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className={`text-base font-bold mt-1 truncate ${accent ? '' : 'text-slate-900'}`} style={accent ? { color: TEAL } : {}}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{sub}</div>}
  </div>
);

const LegendDot = ({ color, label }) => (
  <span className="flex items-center gap-1.5">
    <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
    {label}
  </span>
);

// ====================== Drawer ======================
const DRAWER_TABS = [
  { key: 'overview',  label: 'Overview',  icon: BarChart3 },
  { key: 'content',   label: 'Content',   icon: FileText },
  { key: 'creative',  label: 'Creative',  icon: ImageIcon },
  { key: 'analytics', label: 'Analytics', icon: TrendingUp },
];

const NodeDrawer = ({ node, isAdmin, contentRow, creativeRow, stage, campaignType, allContents, allCreatives, onClose, onEdit, onLinkContent, onCreateContent, onEditContent, onLinkCreative, onUploadCreative, showToast }) => {
  const [tab, setTab] = useState('content');
  const c = performanceColor(node.roi || 0);

  return (
    <>
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />
      <div className="fixed right-0 top-0 bottom-0 z-50 w-full max-w-md bg-white shadow-2xl flex flex-col" data-testid="flow-builder-drawer">
        <div className="flex items-start justify-between p-5 border-b border-slate-200">
          <div className="flex-1 pr-4">
            <div className="flex items-center gap-2 mb-1.5">
              <span className={`inline-block w-2 h-2 rounded-full ${c.dot}`} />
              <span className={`text-[10px] font-semibold uppercase tracking-wider ${c.text}`}>{c.name} performing</span>
              {node.status && node.status !== 'active' && (
                <span className="text-[9px] uppercase font-bold px-1 py-0.5 rounded bg-slate-200 text-slate-600 tracking-wider">{node.status}</span>
              )}
            </div>
            <h2 className="text-lg font-bold text-slate-900 break-words" data-testid="flow-drawer-title">{node.name}</h2>
            <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-[10px] text-slate-500">
              {node.brand && <span><strong>Brand:</strong> {node.brand}</span>}
              {stage && <span><strong>Stage:</strong> {stage.name}</span>}
              {campaignType && <span><strong>Type:</strong> {campaignType.name}</span>}
            </div>
          </div>
          <div className="flex items-center gap-1">
            {isAdmin && (
              <button onClick={onEdit} data-testid="flow-drawer-edit-btn" className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" title="Edit node"><Edit2 size={15} /></button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-500" data-testid="flow-builder-drawer-close"><X size={18} /></button>
          </div>
        </div>

        {/* Tabs */}
        <div className="px-5 border-b border-slate-200 flex gap-0">
          {DRAWER_TABS.map((t) => {
            const Icon = t.icon;
            const active = tab === t.key;
            return (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`flow-drawer-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-semibold border-b-2 transition-colors ${active ? 'text-slate-900' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                style={active ? { borderColor: TEAL } : { borderColor: 'transparent' }}>
                <Icon size={12} />
                {t.label}
              </button>
            );
          })}
        </div>

        <div className="overflow-y-auto flex-1 p-5">
          {tab === 'overview'  && <OverviewTab node={node} />}
          {tab === 'content'   && (
            <ContentTab
              node={node} contentRow={contentRow} allContents={allContents} isAdmin={isAdmin}
              onLink={onLinkContent} onCreate={onCreateContent} onEdit={onEditContent} showToast={showToast}
            />
          )}
          {tab === 'creative'  && (
            <CreativeTab
              node={node} creativeRow={creativeRow} allCreatives={allCreatives} isAdmin={isAdmin}
              onLink={onLinkCreative} onUpload={onUploadCreative}
            />
          )}
          {tab === 'analytics' && <AnalyticsTab node={node} />}
        </div>
      </div>
    </>
  );
};

// ---------- Overview tab ----------
const OverviewTab = ({ node }) => {
  const m = node.metrics || {};
  return (
    <div className="space-y-3" data-testid="drawer-overview-tab">
      <div className="grid grid-cols-2 gap-3">
        <KV label="Revenue"   value={fmtInr(m.revenue)}      accent />
        <KV label="ROAS"      value={`${node.roi || 0}x`}    accent />
        <KV label="Open Rate" value={`${node.seen_rate || 0}%`} />
        <KV label="CTR"       value={`${node.ctr || 0}%`} />
        <KV label="Sent"      value={fmtCompact(m.sent)} />
        <KV label="Delivered" value={fmtCompact(m.delivered)} />
        <KV label="Seen"      value={fmtCompact(m.seen)} />
        <KV label="Clicks"    value={fmtCompact(m.clicks)} />
      </div>
      <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
        <div className="flex items-start gap-2">
          {(node.roi || 0) < 2 ? <AlertTriangle size={14} className="mt-0.5 text-rose-500 flex-shrink-0" /> :
           (node.roi || 0) >= 5 ? <Award size={14} className="mt-0.5 text-emerald-500 flex-shrink-0" /> :
                                   <ArrowRight size={14} className="mt-0.5 text-amber-500 flex-shrink-0" />}
          <p className="text-[11px] text-slate-600 leading-relaxed">
            {(node.roi || 0) >= 5 ? 'Top performer. Consider scaling reach or replicating this flow for similar cohorts.' :
             (node.roi || 0) >= 2 ? 'Average performance. Test creative variations or refine the cohort.' :
                                     'Underperforming or no data. Use Edit to set per-node metrics or upload a CSV via Admin → Upload Center.'}
          </p>
        </div>
      </div>
    </div>
  );
};

// ---------- Content tab ----------
const ContentTab = ({ node, contentRow, allContents, isAdmin, onLink, onCreate, onEdit, showToast }) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const copyRef = useRef(null);

  const filteredContents = useMemo(() => {
    const q = search.toLowerCase();
    return (allContents || []).filter((c) =>
      !q || c.name.toLowerCase().includes(q) || (c.tags || []).some((t) => t.toLowerCase().includes(q))
    );
  }, [allContents, search]);

  const copy = async () => {
    if (!contentRow?.body) return;
    try {
      await navigator.clipboard.writeText(contentRow.body);
      showToast?.('Copied to clipboard', 'success');
    } catch {
      if (copyRef.current) { copyRef.current.select(); document.execCommand('copy'); showToast?.('Copied', 'success'); }
    }
  };

  return (
    <div className="space-y-3" data-testid="drawer-content-tab">
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100">
          <button onClick={() => setLinkOpen((s) => !s)} data-testid="content-link-btn"
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100">
            <LinkIcon size={11} /> {contentRow ? 'Change linked' : 'Link existing'}
          </button>
          <button onClick={onCreate} data-testid="content-create-btn"
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50">
            <Plus size={11} /> Create new
          </button>
          {contentRow && (
            <>
              <button onClick={() => onEdit(contentRow)} data-testid="content-edit-btn"
                className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50">
                <Edit2 size={11} /> Edit
              </button>
              <button onClick={() => onLink(null)} data-testid="content-unlink-btn"
                className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-rose-600 bg-white border border-rose-200 rounded-md hover:bg-rose-50">
                <X size={11} /> Unlink
              </button>
            </>
          )}
        </div>
      )}

      {linkOpen && isAdmin && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-2" data-testid="content-link-panel">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
              placeholder="Search content by name or tag..."
              data-testid="content-link-search"
              className="w-full pl-7 pr-2 py-1.5 text-[11px] border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div className="max-h-40 overflow-y-auto space-y-1">
            {filteredContents.length === 0 ? (
              <p className="text-[10px] text-slate-400 px-1">No content found.</p>
            ) : filteredContents.map((c) => (
              <button key={c.id} onClick={() => { onLink(c.id); setLinkOpen(false); }}
                data-testid={`content-link-option-${c.name}`}
                className="w-full text-left px-2 py-1.5 text-[11px] hover:bg-white rounded-md flex items-center justify-between">
                <span className="truncate"><strong>{c.name}</strong> <span className="text-slate-400">v{c.version}</span></span>
                <span className="text-[9px] text-slate-400 ml-2">{(c.tags || []).slice(0, 2).join(', ')}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      {!contentRow ? (
        <div className="text-center py-6 px-4 border-2 border-dashed border-slate-200 rounded-xl">
          <MessageSquare size={20} className="mx-auto mb-1.5 text-slate-300" />
          <p className="text-xs font-semibold text-slate-700">No linked content</p>
          <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
            {isAdmin ? 'Link existing or create a new WhatsApp message above.' : 'No content is linked to this campaign yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-[10px] text-slate-500">
            <div className="flex items-center gap-1.5">
              <Clock size={10} />
              Updated {contentRow.updated_at ? new Date(contentRow.updated_at).toLocaleString() : '—'}
            </div>
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-semibold">v{contentRow.version}</span>
              <span>{(contentRow.body || '').length} chars</span>
            </div>
          </div>
          {(contentRow.tags || []).length > 0 && (
            <div className="flex flex-wrap gap-1">
              {contentRow.tags.map((t) => (
                <span key={t} className="text-[10px] px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded-full">#{t}</span>
              ))}
            </div>
          )}
          <div className="relative">
            <div className="bg-[#e7ddd1] rounded-xl p-3"
              style={{ backgroundImage: 'radial-gradient(circle at 25px 25px, rgba(255,255,255,0.2) 2%, transparent 0%), radial-gradient(circle at 75px 75px, rgba(255,255,255,0.15) 2%, transparent 0%)', backgroundSize: '100px 100px' }}>
              <div className="relative bg-white rounded-lg px-3 py-2 shadow-sm max-w-[90%] ml-auto" style={{ borderTopRightRadius: 4 }}>
                <pre className="text-[12px] text-slate-800 whitespace-pre-wrap font-sans leading-relaxed max-h-[260px] overflow-y-auto" data-testid="flow-drawer-content-body">
                  {contentRow.body || <span className="text-slate-400 italic">No content body.</span>}
                </pre>
                <div className="flex items-center justify-end gap-1 mt-1 text-[9px] text-slate-400">
                  <Clock size={8} /><span>now</span><span>✓✓</span>
                </div>
              </div>
            </div>
            <button onClick={copy} data-testid="flow-drawer-content-copy"
              className="absolute top-1 right-1 p-1.5 bg-white border border-slate-200 rounded-md hover:bg-slate-50 shadow-sm" title="Copy content">
              <Copy size={11} className="text-slate-600" />
            </button>
          </div>
          <textarea ref={copyRef} value={contentRow.body || ''} readOnly className="sr-only" />

          {(contentRow.versions || []).length > 1 && (
            <div>
              <button onClick={() => setShowHistory((s) => !s)} data-testid="content-history-toggle"
                className="text-[10px] font-semibold text-teal-700 hover:underline">
                {showHistory ? 'Hide' : 'Show'} version history ({contentRow.versions.length})
              </button>
              {showHistory && (
                <div className="mt-2 space-y-2 max-h-[200px] overflow-y-auto">
                  {contentRow.versions.slice().reverse().map((v) => (
                    <div key={v.version} className="border border-slate-200 rounded-lg p-2">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-semibold text-slate-900">v{v.version}</span>
                        <span className="text-[9px] text-slate-400">{new Date(v.edited_at).toLocaleString()}</span>
                      </div>
                      <pre className="text-[11px] text-slate-600 whitespace-pre-wrap font-sans">{v.body}</pre>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Creative tab ----------
const CreativeTab = ({ node, creativeRow, allCreatives, isAdmin, onLink, onUpload }) => {
  const [linkOpen, setLinkOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [showFull, setShowFull] = useState(false);
  const fileRef = useRef(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return (allCreatives || []).filter((c) => c.status !== 'archived' && (
      !q || c.name.toLowerCase().includes(q) || (c.tags || []).some((t) => t.toLowerCase().includes(q))
    ));
  }, [allCreatives, search]);

  const onPickFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    onUpload(f, node.name);
    if (fileRef.current) fileRef.current.value = '';
  };

  return (
    <div className="space-y-3" data-testid="drawer-creative-tab">
      {isAdmin && (
        <div className="flex flex-wrap items-center gap-2 pb-2 border-b border-slate-100">
          <button onClick={() => setLinkOpen((s) => !s)} data-testid="creative-link-btn"
            className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-md hover:bg-teal-100">
            <LinkIcon size={11} /> {creativeRow ? 'Change linked' : 'Link existing'}
          </button>
          <label className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-slate-700 bg-white border border-slate-300 rounded-md hover:bg-slate-50 cursor-pointer">
            <UploadIcon size={11} /> Upload new
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp,.gif,.svg" className="hidden" onChange={onPickFile} data-testid="creative-tab-upload-file" />
          </label>
          {creativeRow && (
            <button onClick={() => onLink(null)} data-testid="creative-unlink-btn"
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-semibold text-rose-600 bg-white border border-rose-200 rounded-md hover:bg-rose-50">
              <X size={11} /> Remove
            </button>
          )}
        </div>
      )}

      {linkOpen && isAdmin && (
        <div className="bg-slate-50 border border-slate-200 rounded-lg p-2 space-y-2" data-testid="creative-link-panel">
          <div className="relative">
            <Search size={12} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search creatives by name or tag..."
              data-testid="creative-link-search"
              className="w-full pl-7 pr-2 py-1.5 text-[11px] border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500" />
          </div>
          <div className="max-h-44 overflow-y-auto grid grid-cols-3 gap-1.5">
            {filtered.length === 0 ? (
              <p className="text-[10px] text-slate-400 col-span-3 text-center py-2">No creatives match.</p>
            ) : filtered.map((c) => (
              <button key={c.id} onClick={() => { onLink(c.id); setLinkOpen(false); }}
                data-testid={`creative-link-option-${c.name}`}
                className="text-left bg-white border border-slate-200 hover:border-teal-400 rounded-md overflow-hidden group">
                <div className="aspect-square bg-slate-50 flex items-center justify-center">
                  <img src={`${API_BASE}${c.url}`} alt={c.name} className="w-full h-full object-contain p-1" loading="lazy" />
                </div>
                <div className="px-1.5 py-1 text-[9px] font-semibold text-slate-700 truncate">{c.name}</div>
              </button>
            ))}
          </div>
        </div>
      )}

      {!creativeRow ? (
        <div className="text-center py-8 px-4 border-2 border-dashed border-slate-200 rounded-xl">
          <ImageIcon size={22} className="mx-auto mb-1.5 text-slate-300" />
          <p className="text-xs font-semibold text-slate-700">No linked creative</p>
          <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
            {isAdmin ? 'Link an existing creative or upload a new image above.' : 'No creative has been linked yet.'}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-center justify-between text-[10px] text-slate-500 mb-1">
            <div className="flex items-center gap-1.5">
              <Clock size={10} />
              Uploaded {creativeRow.created_at ? new Date(creativeRow.created_at).toLocaleString() : '—'}
            </div>
            <div className="flex items-center gap-2">
              <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-semibold">v{creativeRow.version}</span>
              <span className="px-1.5 py-0.5 bg-slate-100 rounded text-slate-600 font-semibold uppercase">{creativeRow.ext}</span>
            </div>
          </div>
          <div className="bg-slate-50 border border-slate-200 rounded-xl overflow-hidden">
            <button onClick={() => setShowFull(true)} className="block w-full" data-testid="creative-tab-preview-btn">
              <img src={`${API_BASE}${creativeRow.url}`} alt={creativeRow.name}
                className="w-full max-h-[280px] object-contain bg-white" />
            </button>
          </div>
          <div className="space-y-1">
            <div className="text-[12px] font-semibold text-slate-900">{creativeRow.name}</div>
            {creativeRow.filename && <div className="text-[10px] text-slate-500">{creativeRow.filename} · {((creativeRow.size_bytes || 0) / 1024).toFixed(1)} KB</div>}
            {(creativeRow.tags || []).length > 0 && (
              <div className="flex flex-wrap gap-1">
                {creativeRow.tags.map((t) => (
                  <span key={t} className="text-[10px] px-1.5 py-0.5 bg-teal-50 text-teal-700 rounded-full">#{t}</span>
                ))}
              </div>
            )}
            {creativeRow.notes && (
              <p className="text-[11px] text-slate-600 mt-1 leading-relaxed border-l-2 border-slate-200 pl-2">{creativeRow.notes}</p>
            )}
          </div>
          <button onClick={() => setShowFull(true)} data-testid="creative-tab-view-full"
            className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-semibold text-teal-700 bg-teal-50 border border-teal-200 rounded-lg hover:bg-teal-100">
            <ExternalLink size={11} /> View full image
          </button>
          {showFull && (
            <div className="fixed inset-0 z-[60] bg-black/80 flex items-center justify-center p-4" onClick={() => setShowFull(false)}
              data-testid="creative-fullscreen-modal">
              <button className="absolute top-4 right-4 p-2 rounded-full bg-white/10 hover:bg-white/20 text-white" onClick={() => setShowFull(false)}>
                <X size={20} />
              </button>
              <img src={`${API_BASE}${creativeRow.url}`} alt={creativeRow.name}
                className="max-w-[90vw] max-h-[90vh] object-contain rounded-lg shadow-2xl"
                onClick={(e) => e.stopPropagation()} />
            </div>
          )}
        </>
      )}
    </div>
  );
};

// ---------- Analytics tab (Recharts) ----------
const AnalyticsTab = ({ node }) => {
  const monthly = (node.monthly_metrics || []).slice().sort((a, b) => (a.month || 0) - (b.month || 0));
  const data = monthly.map((m) => {
    const roi = m.spend > 0 ? +(m.revenue / m.spend).toFixed(2) : 0;
    return { month: m.month_name || MONTH_NAMES[(m.month || 1) - 1], revenue: m.revenue || 0, roas: roi, clicks: m.clicks || 0 };
  });

  if (data.length === 0) {
    return (
      <div className="text-center py-10 border-2 border-dashed border-slate-200 rounded-xl" data-testid="drawer-analytics-tab">
        <TrendingUp size={24} className="mx-auto mb-2 text-slate-300" />
        <p className="text-sm font-semibold text-slate-700">No trend data available</p>
        <p className="text-[10px] text-slate-500 mt-1 leading-relaxed">
          Edit this node and add Monthly Metrics, or upload a CSV via <strong>Admin → Upload Center</strong>.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-5" data-testid="drawer-analytics-tab">
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">Monthly metrics</div>
        <div className="rounded-lg border border-slate-200 overflow-hidden">
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase text-slate-500">
              <tr>
                <th className="px-3 py-2 text-left">Month</th>
                <th className="px-3 py-2 text-right">Revenue</th>
                <th className="px-3 py-2 text-right">ROAS</th>
                <th className="px-3 py-2 text-right">Clicks</th>
              </tr>
            </thead>
            <tbody>
              {data.map((d) => (
                <tr key={d.month} className="border-t border-slate-100">
                  <td className="px-3 py-1.5 font-medium text-slate-700">{d.month}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtInr(d.revenue)}</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{d.roas}x</td>
                  <td className="px-3 py-1.5 text-right tabular-nums">{fmtCompact(d.clicks)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <TrendChart title="Revenue trend" data={data} dataKey="revenue" color={TEAL}   fmt={fmtInr} testid="drawer-revenue-trend" />
      <TrendChart title="ROAS trend"    data={data} dataKey="roas"    color={PURPLE} fmt={(v) => `${v}x`} testid="drawer-roas-trend" />
    </div>
  );
};

const TrendChart = ({ title, data, dataKey, color, fmt, testid }) => (
  <div data-testid={testid}>
    <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-2">{title}</div>
    <div className="bg-slate-50 rounded-lg border border-slate-200 p-2" style={{ height: 160 }}>
      <ResponsiveContainer width="100%" height="100%">
        <LineChart data={data} margin={{ top: 8, right: 12, bottom: 4, left: -10 }}>
          <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" vertical={false} />
          <XAxis dataKey="month" tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} />
          <YAxis tick={{ fontSize: 10, fill: '#64748b' }} axisLine={false} tickLine={false} tickFormatter={(v) => fmt(v).replace('₹','₹').replace(/\.00$/, '')} width={50} />
          <RTooltip
            contentStyle={{ background: 'white', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 11, padding: '6px 10px' }}
            labelStyle={{ color: '#64748b', fontWeight: 600, fontSize: 10 }}
            formatter={(v) => [fmt(v), title.replace(' trend','')]}
          />
          <Line type="monotone" dataKey={dataKey} stroke={color} strokeWidth={2}
            dot={{ r: 3, fill: color, strokeWidth: 0 }}
            activeDot={{ r: 5, fill: color, stroke: 'white', strokeWidth: 2 }} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const KV = ({ label, value, accent }) => (
  <div className="bg-white border border-slate-200 rounded-lg px-3 py-2">
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    <div className="text-sm font-bold mt-0.5 tabular-nums" style={accent ? { color: TEAL } : { color: '#0f172a' }}>{value}</div>
  </div>
);

// ====================== Modals ======================
// Format a raw metric value for display in the read-only totals grid.
const fmtTotal = (key, v) => {
  const n = Number(v || 0);
  if (key === 'revenue' || key === 'spend') {
    if (n >= 1e7) return `₹${(n / 1e7).toFixed(2)}Cr`;
    if (n >= 1e5) return `₹${(n / 1e5).toFixed(2)}L`;
    if (n >= 1e3) return `₹${(n / 1e3).toFixed(1)}K`;
    return `₹${n.toLocaleString('en-IN')}`;
  }
  return n.toLocaleString('en-IN');
};

const ReadOnlyMetric = ({ label, value, testid, accent }) => (
  <div
    className={`rounded-lg px-2.5 py-2 border ${accent ? 'bg-teal-50 border-teal-200' : 'bg-slate-50 border-slate-200'}`}
    data-testid={testid}
  >
    <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">{label}</div>
    <div className={`text-sm font-bold mt-0.5 tabular-nums ${accent ? 'text-teal-700' : 'text-slate-900'}`}>{value}</div>
  </div>
);

// Build a chronological list of {month, year, label} options starting Jan 2025
// through Dec of (current year + 3). Used by the combined Month+Year dropdown.
const MONTH_YEAR_OPTIONS = (() => {
  const opts = [];
  const startYear = 2025;
  const endYear = new Date().getFullYear() + 3;
  for (let y = startYear; y <= endYear; y += 1) {
    for (let m = 1; m <= 12; m += 1) {
      opts.push({ month: m, year: y, label: `${MONTH_NAMES[m - 1]} ${y}` });
    }
  }
  return opts;
})();

// Sum monthly rows into a totals dict + compute derived rates.
const summarizeMonthly = (rows) => {
  const totals = { revenue: 0, spend: 0, sent: 0, delivered: 0, seen: 0, clicks: 0 };
  (rows || []).forEach((r) => {
    METRIC_KEYS.forEach((k) => { totals[k] += Number(r[k] || 0); });
  });
  return {
    ...totals,
    ctr: totals.delivered > 0 ? (totals.clicks / totals.delivered) * 100 : 0,
    delivery_rate: totals.sent > 0 ? (totals.delivered / totals.sent) * 100 : 0,
    roas: totals.spend > 0 ? totals.revenue / totals.spend : 0,
  };
};

const NodeEditModal = ({ node, stages, types, contents, creatives, onClose, onSave }) => {
  const [draft, setDraft] = useState({
    ...node,
    type_id: node.type_id || '',
    content_id: node.content_id || '',
    creative_id: node.creative_id || '',
    stage_id: node.stage_id || '',
    metrics: node.metrics || { revenue:0, spend:0, sent:0, delivered:0, seen:0, clicks:0 },
    monthly_metrics: (node.monthly_metrics || []).map((m) => ({
      // Backfill year for any legacy monthly row that pre-dates the Month+Year change
      year: m.year || 2025,
      month: m.month,
      month_name: m.month_name || MONTH_NAMES[(m.month || 1) - 1],
      revenue: m.revenue || 0, spend: m.spend || 0,
      sent: m.sent || 0, delivered: m.delivered || 0,
      seen: m.seen || 0, clicks: m.clicks || 0,
    })),
  });
  const isNew = !!node._isNew;
  const canSave = (draft.name || '').trim().length > 0;

  // Auto-computed totals — the single source of truth is monthly_metrics.
  const summary = React.useMemo(() => summarizeMonthly(draft.monthly_metrics), [draft.monthly_metrics]);

  const sortMonthly = (rows) => [...rows].sort((a, b) => (a.year - b.year) || (a.month - b.month));

  const addMonth = () => {
    const used = new Set((draft.monthly_metrics || []).map((m) => `${m.year}-${m.month}`));
    const next = MONTH_YEAR_OPTIONS.find((o) => !used.has(`${o.year}-${o.month}`));
    if (!next) return;
    setDraft({
      ...draft,
      monthly_metrics: sortMonthly([...(draft.monthly_metrics || []), {
        year: next.year, month: next.month, month_name: MONTH_NAMES[next.month - 1],
        revenue: 0, spend: 0, sent: 0, delivered: 0, seen: 0, clicks: 0,
      }]),
    });
  };
  const updateMonthYear = (idx, value) => {
    // value = "YYYY-M" string from the dropdown
    const [ys, ms] = value.split('-');
    const y = Number(ys); const m = Number(ms);
    const dupIdx = (draft.monthly_metrics || []).findIndex((row, i) => i !== idx && row.year === y && row.month === m);
    if (dupIdx !== -1) {
      // eslint-disable-next-line no-alert
      window.alert(`${MONTH_NAMES[m - 1]} ${y} is already in this campaign. Pick a different month.`);
      return;
    }
    const copy = [...(draft.monthly_metrics || [])];
    copy[idx] = { ...copy[idx], year: y, month: m, month_name: MONTH_NAMES[m - 1] };
    setDraft({ ...draft, monthly_metrics: sortMonthly(copy) });
  };
  const updateMonthMetric = (idx, field, value) => {
    const copy = [...(draft.monthly_metrics || [])];
    copy[idx] = { ...copy[idx], [field]: Number(value) || 0 };
    setDraft({ ...draft, monthly_metrics: copy });
  };
  const removeMonth = (idx) => {
    const copy = [...(draft.monthly_metrics || [])];
    copy.splice(idx, 1);
    setDraft({ ...draft, monthly_metrics: copy });
  };

  const submit = () => {
    // Snap totals to monthly-derived values before saving so the payload is
    // internally consistent. Backend will re-derive too, but this keeps the
    // request body honest.
    const totals = METRIC_KEYS.reduce((acc, k) => ({ ...acc, [k]: Number(summary[k]) || 0 }), {});
    onSave({ ...draft, metrics: totals });
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="node-edit-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h3 className="text-base font-semibold text-slate-900">{isNew ? 'New campaign node' : 'Edit campaign node'}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500" data-testid="node-edit-close"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-4">
          {/* Basics */}
          <div className="grid grid-cols-2 gap-3">
            <ModalInput label="Campaign Name *" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="node-name-input" />
            <ModalInput label="Brand" value={draft.brand} onChange={(v) => setDraft({ ...draft, brand: v })} testid="node-brand-input" />
            <ModalSelect label="Journey Stage" value={draft.stage_id} onChange={(v) => setDraft({ ...draft, stage_id: v })} testid="node-stage-select">
              <option value="">— Unassigned —</option>
              {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
            </ModalSelect>
            <ModalSelect label="Campaign Type" value={draft.type_id} onChange={(v) => setDraft({ ...draft, type_id: v })} testid="node-type-select">
              <option value="">— None —</option>
              {types.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
            </ModalSelect>
            <ModalSelect label="Campaign Content" value={draft.content_id} onChange={(v) => setDraft({ ...draft, content_id: v })} testid="node-content-select">
              <option value="">— None —</option>
              {contents.map((c) => <option key={c.id} value={c.id}>{c.name} (v{c.version})</option>)}
            </ModalSelect>
            <ModalSelect label="Creative" value={draft.creative_id} onChange={(v) => setDraft({ ...draft, creative_id: v })} testid="node-creative-select">
              <option value="">— None —</option>
              {(creatives || []).filter((c) => c.status !== 'archived').map((c) => <option key={c.id} value={c.id}>{c.name} (v{c.version})</option>)}
            </ModalSelect>
            <ModalSelect label="Status" value={draft.status} onChange={(v) => setDraft({ ...draft, status: v })} testid="node-status-select">
              <option value="active">Active</option>
              <option value="paused">Paused</option>
              <option value="archived">Archived</option>
            </ModalSelect>
          </div>

          {/* Totals — read-only, auto-summed from Monthly Metrics */}
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500 mb-2 flex items-center justify-between">
              <span>Totals — auto-summed from Monthly Metrics</span>
              <span className="text-[9px] font-normal normal-case tracking-normal text-slate-400 italic">Read-only</span>
            </div>
            <div className="grid grid-cols-3 gap-2">
              {METRIC_KEYS.map((k) => (
                <ReadOnlyMetric key={k} label={k.charAt(0).toUpperCase() + k.slice(1)}
                  value={fmtTotal(k, summary[k])}
                  testid={`node-metric-${k}`} />
              ))}
            </div>
            {/* Derived — CTR / Delivery Rate / ROAS */}
            <div className="grid grid-cols-3 gap-2 mt-2">
              <ReadOnlyMetric label="CTR"           value={`${summary.ctr.toFixed(2)}%`}           testid="node-derived-ctr" accent />
              <ReadOnlyMetric label="Delivery Rate" value={`${summary.delivery_rate.toFixed(1)}%`} testid="node-derived-delivery-rate" accent />
              <ReadOnlyMetric label="ROAS"          value={`${summary.roas.toFixed(2)}x`}          testid="node-derived-roas" accent />
            </div>
          </div>

          {/* Monthly */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-slate-500">Monthly metrics <span className="normal-case tracking-normal text-slate-400 italic ml-1">(source of truth)</span></div>
              <button onClick={addMonth} disabled={(draft.monthly_metrics || []).length >= MONTH_YEAR_OPTIONS.length}
                data-testid="node-monthly-add"
                className="text-[10px] font-semibold text-teal-700 hover:underline disabled:opacity-40 disabled:hover:no-underline">
                + Add month
              </button>
            </div>
            {(draft.monthly_metrics || []).length === 0 ? (
              <p className="text-[10px] text-slate-400 italic">No monthly rows yet. Add one to power the trend charts and totals.</p>
            ) : (
              <div className="space-y-1.5 max-h-[240px] overflow-y-auto pr-1">
                <div className="grid grid-cols-[110px_repeat(6,1fr)_18px] gap-1 text-[9px] font-bold uppercase tracking-wider text-slate-400 px-1">
                  <div>Month + Year</div>
                  {METRIC_KEYS.map((k) => <div key={k} className="text-right">{k.slice(0, 4)}</div>)}
                  <div />
                </div>
                {(draft.monthly_metrics || []).map((m, idx) => (
                  <div key={`${m.year}-${m.month}-${idx}`} className="grid grid-cols-[110px_repeat(6,1fr)_18px] gap-1 items-center" data-testid={`node-monthly-row-${idx}`}>
                    <select
                      value={`${m.year}-${m.month}`}
                      onChange={(e) => updateMonthYear(idx, e.target.value)}
                      className="px-1 py-1 text-[10px] border border-slate-300 rounded"
                      data-testid={`node-monthly-my-${idx}`}
                    >
                      {MONTH_YEAR_OPTIONS.map((o) => (
                        <option key={`${o.year}-${o.month}`} value={`${o.year}-${o.month}`}>{o.label}</option>
                      ))}
                    </select>
                    {METRIC_KEYS.map((k) => (
                      <input key={k} type="number" value={m[k] ?? 0}
                        onChange={(e) => updateMonthMetric(idx, k, e.target.value)}
                        className="px-1 py-1 text-[10px] border border-slate-300 rounded text-right tabular-nums w-full" />
                    ))}
                    <button onClick={() => removeMonth(idx)} className="text-rose-500 hover:text-rose-700"><Trash2 size={11} /></button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-200 rounded-lg">Cancel</button>
            <button onClick={submit} disabled={!canSave}
              data-testid="node-edit-save"
              className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90"
              style={{ backgroundColor: TEAL }}>
              {isNew ? 'Create node' : 'Save changes'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const MoveNodeModal = ({ node, stages, currentStageId, onClose, onSubmit }) => {
  const [stageId, setStageId] = useState(currentStageId || '');
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="node-move-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl w-full max-w-md shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Move &quot;{node.name}&quot;</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <ModalSelect label="Move to stage" value={stageId} onChange={setStageId} testid="node-move-stage-select">
            <option value="">— Unassigned —</option>
            {stages.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
          </ModalSelect>
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-200 rounded-lg">Cancel</button>
            <button onClick={() => onSubmit(stageId)} data-testid="node-move-submit"
              className="px-4 py-2 text-sm text-white rounded-lg hover:opacity-90" style={{ backgroundColor: TEAL }}>
              Move
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ContentEditModal = ({ content, onClose, onSave }) => {
  const [draft, setDraft] = useState({
    id: content.id || null,
    name: content.name || '',
    body: content.body || '',
    tags: Array.isArray(content.tags) ? content.tags.join(', ') : (content.tags || ''),
  });
  const canSave = (draft.name || '').trim() && (draft.body || '').trim();
  const isNew = !content.id;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="content-edit-modal" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl">
        <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">{isNew ? 'New content' : 'Edit content'}</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100 text-slate-500"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <ModalInput label="Name *" value={draft.name} onChange={(v) => setDraft({ ...draft, name: v })} testid="content-name-input" />
          <div>
            <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">Body *</label>
            <textarea value={draft.body} onChange={(e) => setDraft({ ...draft, body: e.target.value })}
              data-testid="content-body-input"
              className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500 font-mono"
              rows={6} placeholder="Hi {name}!&#10;Your cart is waiting..." />
            <div className="text-[10px] text-slate-400 text-right mt-1">{(draft.body || '').length} chars</div>
          </div>
          <ModalInput label="Tags (comma-separated)" value={draft.tags} onChange={(v) => setDraft({ ...draft, tags: v })} testid="content-tags-input" />
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-4 py-2 text-sm bg-slate-200 rounded-lg">Cancel</button>
            <button onClick={() => onSave(draft)} disabled={!canSave}
              data-testid="content-edit-save"
              className="px-4 py-2 text-sm text-white rounded-lg disabled:opacity-50 hover:opacity-90"
              style={{ backgroundColor: TEAL }}>
              {isNew ? 'Create' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const ModalInput = ({ label, value, onChange, placeholder, testid, type }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
    <input type={type || 'text'} value={value || (type === 'number' ? '0' : '')}
      onChange={(e) => onChange(e.target.value)} placeholder={placeholder} data-testid={testid}
      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500" />
  </div>
);

const ModalSelect = ({ label, value, onChange, children, testid }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
    <select value={value || ''} onChange={(e) => onChange(e.target.value)} data-testid={testid}
      className="w-full px-3 py-2 text-sm border border-slate-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-teal-500">
      {children}
    </select>
  </div>
);

export default FlowBuilder;
